import { useState, useCallback, useMemo } from 'react'
import { IUserMotorCarrier, ACTIVE_MOTOR_CARRIER_STATES } from 'apps/fleet-planner/types'
import {
  fetchAllUserMotorCarriers,
  submitRemoveMotorCarriers,
} from 'apps/fleet-planner/utils/api/carriers'
import { IMotorCarrierUserForCurrentUserFragment } from './graphql/MotorCarrierUserForCurrentUserFragment'
import { useMotorCarrierUsersForCurrentUserLazyQuery } from './graphql/MotorCarrierUsersForCurrentUser'

export interface IUseCarrierMembershipApiState {
  /**
   * @deprecated Use `fetchMotorCarrierUsers` instead.
   */
  getUserMotorCarriers: () => void
  removeMotorCarrier: (id: number) => Promise<void>
  /**
   * @deprecated Use `motorCarrierUsers` instead.
   */
  userMotorCarriers: IUserMotorCarrier[]
  activeUserMotorCarriers: IUserMotorCarrier[]
  isUserMotorCarriersLoading: boolean
  errors?: string
  /**
   * @description Fetch `MotorCarrierUsers` that are associated with the current user.
   */
  fetchMotorCarrierUsers: () => void
  /**
   * @description Have `MotorCarrierUsers` for the current user been fetched?
   */
  hasLoadedMotorCarrierUsers: boolean
  /**
   * @description `MotorCarrierUsers` that are associated with the current user. Each item contains information about
   * the user's relationship to a carrier (e.g., type, role), in addition to the carrier's basic information.
   */
  motorCarrierUsers: IMotorCarrierUserForCurrentUserFragment[]
}

export interface IUseCarrierMembershipApiProps {
  onGetUserMotorCarriersComplete?: (motorCarriers: IUserMotorCarrier[]) => void
}

// Hook used to manage getUserMotorCarriers
export const useCarrierMembershipApi = ({
  onGetUserMotorCarriersComplete,
}: IUseCarrierMembershipApiProps = {}): IUseCarrierMembershipApiState => {
  const [userMotorCarriers, setUserMotorCarriers] = useState<IUserMotorCarrier[]>([])

  const [fetchMotorCarrierUsers, { called, data }] = useMotorCarrierUsersForCurrentUserLazyQuery()
  const hasLoadedMotorCarrierUsers = called && Boolean(data)

  const { motorCarrierUsersForUser: motorCarrierUsers = [] } = data ?? {}

  const activeUserMotorCarriers = useMemo(
    () =>
      userMotorCarriers.filter(userMotorCarrier =>
        ([...ACTIVE_MOTOR_CARRIER_STATES] as string[]).includes(
          userMotorCarrier.carrierApplication?.state || 'unknown'
        )
      ),
    [userMotorCarriers]
  )
  const [errors, setErrors] = useState<string | undefined>()
  const [isUserMotorCarriersLoading, setIsUserMotorCarriersLoading] = useState<boolean>(false)

  const getUserMotorCarriers = useCallback(async (): Promise<void> => {
    setErrors(undefined)
    setIsUserMotorCarriersLoading(true)
    try {
      const { motorCarriersForUser: response } = await fetchAllUserMotorCarriers()
      setUserMotorCarriers(response)
      if (onGetUserMotorCarriersComplete) {
        onGetUserMotorCarriersComplete(response)
      }
    } catch (e) {
      setErrors(e)
    }
    setIsUserMotorCarriersLoading(false)
  }, [onGetUserMotorCarriersComplete])

  const removeMotorCarrier = useCallback(async (id: number): Promise<void> => {
    try {
      setErrors(undefined)
      await submitRemoveMotorCarriers(id)
    } catch (e) {
      setErrors(e)
    }
  }, [])

  return {
    getUserMotorCarriers,
    removeMotorCarrier,
    userMotorCarriers,
    activeUserMotorCarriers,
    isUserMotorCarriersLoading,
    errors,
    fetchMotorCarrierUsers,
    hasLoadedMotorCarrierUsers,
    motorCarrierUsers,
  }
}
